import React, { useState, useEffect } from 'react';
import Swal from 'sweetalert2';
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Card,
  CardContent,
  Divider,
  CircularProgress
} from '@mui/material';
import {
  Router as OnuIcon,
  Close as CloseIcon,
  Settings as SettingsIcon,
  Business as BusinessIcon
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import api from '../../services/useApi';
import equipmentService from '../../services/equipmentService';
import PerfisOnuSelect from '../selects/PerfisOnuSelect';

interface Equipment {
  id: number;
  name: string;
  gpon_sn: string;
  mac_address: string;
  model: string;
  provision_name?: string;
}

interface CTO {
  id: number;
  name: string;
  address: string;
  olt_id: number;
}

interface ProvisionarModalProps {
  open: boolean;
  onClose: () => void;
  equipment: Equipment | null;
  cto: CTO | null;
  onProvisionSuccess: () => void;
}

interface ProvisionData {
  perfilId: string | number;
  provisionName: string;
}

const ProvisionarModal: React.FC<ProvisionarModalProps> = ({
  open,
  onClose,
  equipment,
  cto,
  onProvisionSuccess
}) => {
  const [loading, setLoading] = useState(false);
  const [provisionData, setProvisionData] = useState<ProvisionData>({
    perfilId: '',
    provisionName: ''
  });


  const handleInputChange = (field: keyof ProvisionData, value: string) => {
    setProvisionData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleProvision = async () => {
    if (!equipment || !cto) return;

    if (!provisionData.perfilId) {
      toast.error('O perfil é obrigatório');
      return;
    }

    setLoading(true);
    try {
      const response = await equipmentService.provisionarEquipamento(
        equipment.id,
        Number(provisionData.perfilId),
        provisionData.provisionName
      );

      if (response.success) {
        toast.success(response.message);

        if (response.debug_result) {
          onProvisionSuccess();
          handleClose();
          Swal.fire({
            title: 'Equipamento Provisionado',
            html: `<p style="text-align: left; margin-bottom: 8px;">${response.message}</p>
                  <p style="text-align: left; margin-bottom: 10px;">Comando finalizado na OLT:</p>
                   <pre style="text-align: left; background: #2b2b2b; color: #72f542; padding: 10px; border-radius: 5px; font-size: 12px; overflow-x: auto;">${response.debug_result}</pre>`,
            icon: 'success',
            width: '600px',
            customClass: {
              container: 'swal-high-zindex'
            }
          });
        } else {
          onProvisionSuccess();
          handleClose();
        }
      } else {
        if (response.debug_result) {
          handleClose();
          Swal.fire({
            title: 'Erro de Provisionamento',
            html: `<p style="text-align: left; margin-bottom: 10px;">${response.error || response.message || 'Falha ao provisionar na OLT.'}</p>
                   <pre style="text-align: left; background: #2b2b2b; color: #fff; padding: 10px; border-radius: 5px; font-size: 12px; overflow-x: auto;">${response.debug_result}</pre>`,
            icon: 'error',
            width: '600px',
            customClass: {
              container: 'swal-high-zindex'
            }
          });
        } else {
          toast.error(response.error || response.message || 'Erro ao provisionar equipamento');
        }
      }
    } catch (err: any) {
      console.error('Erro ao provisionar equipamento:', err);
      const errorMsg = err?.response?.data?.error || err?.response?.data?.message || err?.message || 'Erro ao provisionar equipamento';
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setProvisionData({
      perfilId: '',
      provisionName: ''
    });
    onClose();
  };

  if (!equipment || !cto) {
    return null;
  }

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="xs"
      fullWidth
    >
      <DialogTitle>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box display="flex" alignItems="center" gap={1}>
            <SettingsIcon color="primary" />
            <Typography variant="h6">
              Provisionar Equipamento
            </Typography>
          </Box>
        </Box>
      </DialogTitle>

      <DialogContent>
        <Grid container spacing={2}>


          {/* Informações da CTO */}
          <Grid item xs={12} md={12}>
            <Card variant="outlined">
              <CardContent>

                <Typography variant="body2" color="text.secondary">
                  <strong>Nome:</strong> {cto.name}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  <strong>Endereço:</strong> {cto.address}
                </Typography>

                <Typography variant="body2" color="text.secondary">
                  <strong>Modelo:</strong> {equipment.model}
                </Typography>

                <Typography variant="body2" color="text.secondary">
                  <strong>GPON SN:</strong> {equipment.gpon_sn}
                </Typography>

                <Typography variant="body2" color="text.secondary">
                  <strong>MAC:</strong> {equipment.mac_address}
                </Typography>

                <Typography variant="body2" color="text.secondary">
                  <strong>OLT:</strong> {cto.olt_id}
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12}>
            <Divider sx={{ my: 1 }} />

          </Grid>

          {/* Campos de Provisionamento */}
          <Grid item xs={12}>
            <PerfisOnuSelect
              oltId={cto.olt_id}
              caixaId={cto.id}
              value={provisionData.perfilId}
              onChange={(val) => handleInputChange('perfilId', val.toString())}
              required
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Nome do Provisionamento"
              value={provisionData.provisionName}
              onChange={(e) => handleInputChange('provisionName', e.target.value)}
              placeholder="Digite o nome de identificação na OLT"
              helperText="Nome de identificação na OLT"
            />
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} color="inherit">
          Cancelar
        </Button>
        <Button
          onClick={handleProvision}
          color="primary"
          variant="contained"
          disabled={loading}
          startIcon={loading ? <CircularProgress size={16} /> : <SettingsIcon />}
        >
          {loading ? 'Provisionando...' : 'Provisionar'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ProvisionarModal; 